/* T&S — Cinema layer v2
   Lenis smooth scroll · Morphing cursor · Embroidery stitch drawer
   WebGL displacement (Three.js) · Line-mask reveal · Refined magnetic
   View Transitions · Image-mask peek
   All gated by prefers-reduced-motion + (hover: hover) where appropriate.
*/
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fineHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }
  function waitFor(test, timeoutMs, cb) {
    var t0 = Date.now();
    (function poll() {
      if (test()) return cb(true);
      if (Date.now() - t0 > timeoutMs) return cb(false);
      requestAnimationFrame(poll);
    })();
  }

  /* ──────────────────────────────────────────────
     1. LENIS SMOOTH SCROLL
     ────────────────────────────────────────────── */
  var lenisInstance = null;
  function initLenis() {
    if (reduceMotion) return;
    waitFor(function () { return window.Lenis; }, 6000, function (ok) {
      if (!ok) return;
      var Lenis = window.Lenis;
      lenisInstance = new Lenis({
        duration: 1.2,
        easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
        smoothWheel: true,
        wheelMultiplier: 1,
        touchMultiplier: 1.8,
        infinite: false,
      });
      function raf(time) {
        lenisInstance.raf(time);
        requestAnimationFrame(raf);
      }
      requestAnimationFrame(raf);

      // Sync ScrollTrigger if present
      if (window.gsap && window.ScrollTrigger) {
        lenisInstance.on('scroll', window.ScrollTrigger.update);
        window.gsap.ticker.add(function (t) { lenisInstance.raf(t * 1000); });
        window.gsap.ticker.lagSmoothing(0);
      }

      // Anchor links use Lenis
      document.addEventListener('click', function (e) {
        var a = e.target.closest('a[href^="#"]');
        if (!a) return;
        var id = a.getAttribute('href');
        if (id === '#' || id.length < 2) return;
        var target = document.querySelector(id);
        if (!target) return;
        e.preventDefault();
        lenisInstance.scrollTo(target, { offset: -72 });
      });
    });
  }

  /* ──────────────────────────────────────────────
     2. MORPHING CUSTOM CURSOR
     ────────────────────────────────────────────── */
  function initCursor() {
    if (reduceMotion || !fineHover) return;
    var ptr = document.createElement('div');
    ptr.className = 'kala-pointer';
    var label = document.createElement('span');
    label.className = 'kala-pointer__label';
    ptr.appendChild(label);
    document.body.appendChild(ptr);
    document.documentElement.classList.add('kala-cursor-hide');

    var x = window.innerWidth / 2, y = window.innerHeight / 2;
    var tx = x, ty = y;
    function tick() {
      tx += (x - tx) * 0.22;
      ty += (y - ty) * 0.22;
      ptr.style.setProperty('--px', tx.toFixed(1) + 'px');
      ptr.style.setProperty('--py', ty.toFixed(1) + 'px');
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);

    window.addEventListener('mousemove', function (e) {
      x = e.clientX; y = e.clientY;
      if (!ptr.classList.contains('is-ready')) ptr.classList.add('is-ready');
    }, { passive: true });
    window.addEventListener('mouseout', function (e) {
      if (e.relatedTarget == null) ptr.classList.add('is-hidden');
    });
    window.addEventListener('mouseover', function () {
      ptr.classList.remove('is-hidden');
    });

    function setState(state, text) {
      ptr.classList.remove('is-pill', 'is-arrow');
      label.textContent = '';
      if (state === 'pill') {
        ptr.classList.add('is-pill');
        label.textContent = text || 'View';
      } else if (state === 'arrow') {
        ptr.classList.add('is-arrow');
      }
    }

    // Hover targets
    function bindTargets() {
      document.querySelectorAll('a[href], button, [role="button"]').forEach(function (el) {
        if (el.dataset.cursorBound) return;
        el.dataset.cursorBound = '1';
        var label = el.dataset.cursor;
        el.addEventListener('mouseenter', function () {
          if (label === 'arrow') setState('arrow');
          else if (label) setState('pill', label);
          else if (el.matches('.kala-card')) setState('pill', 'View');
          else if (el.matches('.kala-btn, .kala-product__cta')) setState('arrow');
          else { ptr.style.setProperty('--ps', '1.6'); }
        });
        el.addEventListener('mouseleave', function () {
          ptr.style.setProperty('--ps', '1');
          setState('default');
        });
      });
    }
    bindTargets();
    // Re-bind after async content
    var bindMO = new MutationObserver(function () { bindTargets(); });
    bindMO.observe(document.body, { childList: true, subtree: true });
  }

  /* ──────────────────────────────────────────────
     3. EMBROIDERY STITCH DRAWER
     ────────────────────────────────────────────── */
  function initStitchDrawer() {
    var els = document.querySelectorAll('.kala-stitch');
    if (!els.length) return;
    els.forEach(function (svg) {
      var path = svg.querySelector('.kala-stitch__path');
      if (!path) return;
      var len = path.getTotalLength();
      path.style.setProperty('--len', len);
      path.setAttribute('stroke-dasharray', '3 3');
      path.style.strokeDashoffset = len;
    });

    if (!('IntersectionObserver' in window) || reduceMotion) {
      els.forEach(function (svg) { svg.classList.add('is-drawn'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          var svg = e.target;
          var path = svg.querySelector('.kala-stitch__path');
          var needle = svg.querySelector('.kala-stitch__needle');
          svg.classList.add('is-drawing');
          path.style.strokeDashoffset = '0';
          if (needle) {
            var len = path.getTotalLength();
            var t0 = performance.now();
            var dur = 2400;
            (function follow(now) {
              var p = Math.min(1, (now - t0) / dur);
              var pt = path.getPointAtLength(p * len);
              needle.setAttribute('cx', pt.x);
              needle.setAttribute('cy', pt.y);
              if (p < 1) requestAnimationFrame(follow);
              else svg.classList.remove('is-drawing');
            })(t0);
          }
          svg.classList.add('is-drawn');
          io.unobserve(svg);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.2 });
    els.forEach(function (svg) { io.observe(svg); });
  }

  /* ──────────────────────────────────────────────
     4. LINE-MASK HEADLINE REVEAL
     Wrap children in .kala-line-reveal automatically via data-line-reveal
     ────────────────────────────────────────────── */
  function initLineReveal() {
    // Wrap any [data-line-reveal] children's text nodes
    document.querySelectorAll('[data-line-reveal]').forEach(function (host) {
      if (host.dataset.lineReady) return;
      host.dataset.lineReady = '1';
      var children = Array.from(host.children);
      var raw = children.length ? children : [host];
      raw.forEach(function (child, i) {
        var wrap = document.createElement('span');
        wrap.className = 'kala-line-reveal';
        var inner = document.createElement('span');
        inner.className = 'kala-line-reveal__inner';
        inner.style.setProperty('--li', i);
        // Move child contents into inner
        while (child.firstChild) inner.appendChild(child.firstChild);
        wrap.appendChild(inner);
        child.appendChild(wrap);
      });
    });

    var els = document.querySelectorAll('.kala-line-reveal, .kala-fade-up, .kala-mask-in');
    if (!els.length || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ──────────────────────────────────────────────
     5. WEBGL DISPLACEMENT (Three.js shader on hero)
     Subtle wave that follows the cursor.
     ────────────────────────────────────────────── */
  function initWebGLHero() {
    if (reduceMotion) return;
    var mount = document.querySelector('.kala-hero__webgl');
    if (!mount) return;
    waitFor(function () { return window.THREE; }, 6000, function (ok) {
      if (!ok) return;
      var THREE = window.THREE;
      var w = mount.clientWidth, h = mount.clientHeight;
      var scene = new THREE.Scene();
      var camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
      camera.position.z = 1;
      var renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(w, h);
      renderer.setClearColor(0x000000, 0);
      mount.appendChild(renderer.domElement);

      var uniforms = {
        uTime:   { value: 0 },
        uMouse:  { value: new THREE.Vector2(0.5, 0.5) },
        uRes:    { value: new THREE.Vector2(w, h) },
      };

      var vertex = [
        'varying vec2 vUv;',
        'void main(){ vUv = uv; gl_Position = vec4(position, 1.0); }'
      ].join('\n');

      // Premium subtle: gold ripple emanating slowly from cursor, plus film grain
      var fragment = [
        'precision highp float;',
        'varying vec2 vUv;',
        'uniform float uTime;',
        'uniform vec2 uMouse;',
        'uniform vec2 uRes;',
        '',
        'float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }',
        '',
        'void main(){',
        '  vec2 uv = vUv;',
        '  vec2 ar = vec2(uRes.x / uRes.y, 1.0);',
        '  vec2 m = uMouse;',
        '  float d = distance(uv * ar, m * ar);',
        '  float ripple = sin(d * 28.0 - uTime * 1.4) * 0.5 + 0.5;',
        '  ripple *= smoothstep(0.55, 0.0, d);',
        '  vec3 gold = vec3(0.788, 0.631, 0.290);',
        '  float vignette = smoothstep(1.2, 0.4, length(uv - 0.5));',
        '  float g = hash(uv * uRes + uTime) * 0.04;',
        '  vec3 col = gold * ripple * 0.55 * vignette + g;',
        '  float alpha = ripple * 0.6 * vignette + g * 0.5;',
        '  gl_FragColor = vec4(col, alpha);',
        '}'
      ].join('\n');

      var material = new THREE.ShaderMaterial({
        uniforms: uniforms,
        vertexShader: vertex,
        fragmentShader: fragment,
        transparent: true,
        depthWrite: false,
      });
      var quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
      scene.add(quad);

      var mouse = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 };
      mount.parentNode.addEventListener('mousemove', function (e) {
        var r = mount.getBoundingClientRect();
        mouse.x = (e.clientX - r.left) / r.width;
        mouse.y = 1 - (e.clientY - r.top) / r.height;
      }, { passive: true });

      window.addEventListener('resize', function () {
        w = mount.clientWidth; h = mount.clientHeight;
        renderer.setSize(w, h);
        uniforms.uRes.value.set(w, h);
      });

      var t0 = performance.now();
      function render() {
        mouse.tx += (mouse.x - mouse.tx) * 0.06;
        mouse.ty += (mouse.y - mouse.ty) * 0.06;
        uniforms.uMouse.value.set(mouse.tx, mouse.ty);
        uniforms.uTime.value = (performance.now() - t0) * 0.001;
        renderer.render(scene, camera);
        requestAnimationFrame(render);
      }
      render();
    });
  }

  /* ──────────────────────────────────────────────
     6. REFINED MAGNETIC + GLOW (spring physics)
     ────────────────────────────────────────────── */
  function initMagnetic() {
    if (reduceMotion || !fineHover) return;
    document.querySelectorAll('[data-magnetic], [data-magnetic-glow]').forEach(function (el) {
      var strength = parseFloat(el.dataset.magnetic) || 0.2;
      var rect = null;
      var tx = 0, ty = 0, x = 0, y = 0;
      var raf = null;

      function tick() {
        tx += (x - tx) * 0.16;
        ty += (y - ty) * 0.16;
        el.style.transform = 'translate3d(' + tx.toFixed(2) + 'px,' + ty.toFixed(2) + 'px,0)';
        if (Math.abs(x - tx) > 0.05 || Math.abs(y - ty) > 0.05) {
          raf = requestAnimationFrame(tick);
        } else { raf = null; }
      }

      el.addEventListener('mouseenter', function () {
        rect = el.getBoundingClientRect();
      });
      el.addEventListener('mousemove', function (e) {
        if (!rect) rect = el.getBoundingClientRect();
        var cx = e.clientX - rect.left - rect.width / 2;
        var cy = e.clientY - rect.top - rect.height / 2;
        x = cx * strength;
        y = cy * strength;
        if (!raf) raf = requestAnimationFrame(tick);
        if (el.hasAttribute('data-magnetic-glow')) {
          el.style.setProperty('--mx', ((e.clientX - rect.left) / rect.width * 100).toFixed(1) + '%');
          el.style.setProperty('--my', ((e.clientY - rect.top) / rect.height * 100).toFixed(1) + '%');
        }
      });
      el.addEventListener('mouseleave', function () {
        x = 0; y = 0;
        rect = null;
        if (!raf) raf = requestAnimationFrame(tick);
      });
    });
  }

  /* ──────────────────────────────────────────────
     7. IMAGE-MASK PEEK (product cards)
     Wires data-peek elements: makes second image use clip-path reveal.
     ────────────────────────────────────────────── */
  function initImagePeek() {
    // Already CSS-driven — just attach IO for performance hint on close cards
    var cards = document.querySelectorAll('.kala-card');
    cards.forEach(function (card) {
      // Mark card as data-peek if it has a secondary image
      var second = card.querySelector('.kala-card__img--secondary');
      if (second && !card.hasAttribute('data-peek')) {
        card.setAttribute('data-peek', '');
        second.setAttribute('data-peek-secondary', '');
      }
    });
  }

  /* ──────────────────────────────────────────────
     8. GSAP — refined ScrollTrigger choreography
     ────────────────────────────────────────────── */
  function initGSAP() {
    if (reduceMotion) return;
    waitFor(function () { return window.gsap && window.ScrollTrigger; }, 6000, function (ok) {
      if (!ok) return;
      var gsap = window.gsap;
      var ScrollTrigger = window.ScrollTrigger;
      gsap.registerPlugin(ScrollTrigger);

      // Pin/scrub
      document.querySelectorAll('.kala-pin-section').forEach(function (sec) {
        var track = sec.querySelector('.kala-pin-track');
        var bg = sec.querySelector('.kala-pin-bg img');
        if (track) {
          ScrollTrigger.create({
            trigger: sec, start: 'top top', end: '+=80%',
            pin: track, pinSpacing: false,
          });
        }
        if (bg) {
          gsap.fromTo(bg,
            { scale: 1.22, y: 0 },
            { scale: 1, y: -50, ease: 'none',
              scrollTrigger: { trigger: sec, start: 'top bottom', end: 'bottom top', scrub: true } }
          );
        }
      });

      // Horizontal scroll-jack
      document.querySelectorAll('.kala-hscroll').forEach(function (wrap) {
        var track = wrap.querySelector('.kala-hscroll__track');
        if (!track) return;
        var overflow = track.scrollWidth - wrap.clientWidth;
        if (overflow <= 0) return;
        gsap.to(track, {
          x: -overflow, ease: 'none',
          scrollTrigger: {
            trigger: wrap, start: 'top top',
            end: function () { return '+=' + overflow; },
            pin: true, scrub: 0.8, invalidateOnRefresh: true,
          },
        });
      });

      // Bold stagger: each child rises with a 3D hinge + scale + opacity
      document.querySelectorAll('[data-gsap-stagger]').forEach(function (wrap) {
        Array.prototype.forEach.call(wrap.children, function (c) { c.style.transition = 'none'; });
        gsap.fromTo(wrap.children,
          { y: 80, opacity: 0, scale: 0.92, rotateX: 16 },
          { y: 0, opacity: 1, scale: 1, rotateX: 0,
            transformPerspective: 1100, transformOrigin: '50% 100%',
            duration: 1.15, ease: 'power3.out', stagger: 0.1,
            scrollTrigger: { trigger: wrap, start: 'top 84%', once: true } }
        );
      });

      // GSAP parallax (kept for compat)
      document.querySelectorAll('[data-gsap-parallax]').forEach(function (el) {
        var amt = parseFloat(el.dataset.gsapParallax) || -80;
        gsap.to(el, {
          y: amt, ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
        });
      });

      window.addEventListener('resize', function () { ScrollTrigger.refresh(); });
    });
  }

  /* ──────────────────────────────────────────────
     9. COUNTDOWN DIGIT FLIP (preserved from v1)
     ────────────────────────────────────────────── */
  function initDigitFlip() {
    document.querySelectorAll('[data-kala-countdown]').forEach(function (root) {
      var digits = root.querySelectorAll('[data-days],[data-hours],[data-minutes],[data-seconds]');
      var last = new WeakMap();
      digits.forEach(function (d) {
        d.classList.add('kala-flip__digit');
        last.set(d, d.textContent);
      });
      var obs = new MutationObserver(function (muts) {
        muts.forEach(function (m) {
          var el = m.target.nodeType === 3 ? m.target.parentNode : m.target;
          if (!el || !el.classList || !el.classList.contains('kala-flip__digit')) return;
          var now = el.textContent;
          var prev = last.get(el);
          if (prev != null && prev !== now) {
            el.classList.remove('is-flipping');
            void el.offsetWidth;
            el.classList.add('is-flipping');
          }
          last.set(el, now);
        });
      });
      digits.forEach(function (d) {
        obs.observe(d, { characterData: true, childList: true, subtree: true });
      });
    });
  }

  /* ──────────────────────────────────────────────
    10. VIEW TRANSITIONS for product image continuity
     ────────────────────────────────────────────── */
  function initViewTransitions() {
    if (reduceMotion || !document.startViewTransition) return;
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href]');
      if (!a) return;
      var href = a.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      if (a.target === '_blank' || a.hasAttribute('download')) return;
      try {
        var u = new URL(a.href, location.href);
        if (u.origin !== location.origin) return;
      } catch (_) { return; }
      // Tag the product card image for shared element continuity
      var img = a.querySelector('img');
      if (img && a.matches('.kala-card')) {
        var name = 'product-' + (a.href.split('/').pop() || '').slice(0, 40);
        img.style.viewTransitionName = name;
      }
      e.preventDefault();
      document.startViewTransition(function () { location.href = a.href; });
    });
  }


  /* ──────────────────────────────────────────────
    11. REAL CARD-FLIP COUNTDOWN DIGITS
     ────────────────────────────────────────────── */
  function initCardFlip() {
    document.querySelectorAll('[data-kala-countdown]').forEach(function (root) {
      var slots = root.querySelectorAll('[data-days],[data-hours],[data-minutes],[data-seconds]');
      slots.forEach(function (slot) {
        if (slot.dataset.flipReady) return;
        slot.dataset.flipReady = '1';
        var current = slot.textContent;
        slot.innerHTML = '';
        var card = document.createElement('span');
        card.className = 'kala-flip-card';
        var inner = document.createElement('span');
        inner.className = 'kala-flip-card__inner';
        var front = document.createElement('span');
        front.className = 'kala-flip-card__face kala-flip-card__face--front';
        front.textContent = current;
        var back = document.createElement('span');
        back.className = 'kala-flip-card__face kala-flip-card__face--back';
        back.textContent = current;
        inner.appendChild(front);
        inner.appendChild(back);
        card.appendChild(inner);
        slot.appendChild(card);
      });

      var obs = new MutationObserver(function (muts) {
        muts.forEach(function (m) {
          // The countdown logic likely sets textContent on the slot directly
          // We intercept by watching attribute changes if any, or by polling
          var node = m.target;
          if (node && node.nodeType === 3) node = node.parentNode;
        });
      });

      // Poll-based approach: check each slot's actual countdown value
      // The countdown script writes raw text. We watch and rewrite.
      var lastValues = new Map();
      slots.forEach(function (slot) { lastValues.set(slot, slot.querySelector('.kala-flip-card__face--front')?.textContent || ''); });

      function tick() {
        slots.forEach(function (slot) {
          // The original countdown writes directly to the slot. We need to extract
          // the latest value. Since we replaced the markup, we read the slot text.
          var card = slot.querySelector('.kala-flip-card');
          if (!card) return;
          var front = card.querySelector('.kala-flip-card__face--front');
          var back  = card.querySelector('.kala-flip-card__face--back');
          if (!front || !back) return;

          // The countdown writes to the slot; detect raw text added as a sibling text node
          var rawText = '';
          slot.childNodes.forEach(function (n) {
            if (n.nodeType === 3 && n.textContent.trim()) rawText = n.textContent.trim();
          });
          if (!rawText) return;
          if (rawText === lastValues.get(slot)) return;

          // Move new value into back face, animate flip, then sync front
          back.textContent = rawText;
          card.classList.remove('is-flipping');
          void card.offsetWidth;
          card.classList.add('is-flipping');
          setTimeout(function () {
            front.textContent = rawText;
            card.classList.remove('is-flipping');
            // Reset rotation instantly so back is hidden again
          }, 700);

          // Clear stray text nodes from the slot
          slot.childNodes.forEach(function (n) {
            if (n.nodeType === 3) slot.removeChild(n);
          });

          lastValues.set(slot, rawText);
        });
        requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    });
  }

  /* ──────────────────────────────────────────────
    12. NAMED VIEW TRANSITIONS — image continuity
     ────────────────────────────────────────────── */
  function applyVTNames() {
    function slug(href) {
      try {
        var u = new URL(href, location.href);
        return u.pathname.split('/').filter(Boolean).pop() || '';
      } catch (_) { return ''; }
    }
    document.querySelectorAll('.kala-card').forEach(function (card) {
      var img = card.querySelector('.kala-card__img--primary, .kala-card__img');
      var href = card.getAttribute('href');
      if (img && href) {
        var name = 'product-img-' + slug(href).replace(/[^a-z0-9-]/gi, '').slice(0, 40);
        img.style.viewTransitionName = name;
      }
    });
    var mainImg = document.querySelector('[data-main-image]');
    if (mainImg) {
      var name = 'product-img-' + slug(location.pathname).replace(/[^a-z0-9-]/gi, '').slice(0, 40);
      mainImg.style.viewTransitionName = name;
    }
  }

  /* ──────────────────────────────────────────────
    13. LIGHTBOX (replaces 3D viewer)
     ────────────────────────────────────────────── */
  function initLightbox() {
    var box = document.querySelector('[data-lightbox]');
    if (!box) return;
    var slides = Array.from(box.querySelectorAll('[data-lightbox-slide]'));
    if (!slides.length) return;
    var stage = box.querySelector('[data-lightbox-stage]');
    var prevBtn = box.querySelector('[data-lightbox-prev]');
    var nextBtn = box.querySelector('[data-lightbox-next]');
    var closeBtn = box.querySelector('[data-lightbox-close]');
    var counter = box.querySelector('[data-lightbox-counter]');
    var triggers = document.querySelectorAll('[data-lightbox-trigger]');
    var thumbs = document.querySelectorAll('[data-thumb]');

    var idx = 0;
    var total = slides.length;

    function pad(n) { return n < 10 ? '0' + n : '' + n; }
    function setIndex(i) {
      idx = (i + total) % total;
      slides.forEach(function (s, k) { s.classList.toggle('is-active', k === idx); });
      // Upgrade the active slide to its full-res src once visible
      var active = slides[idx];
      if (active && active.dataset.full && active.src !== active.dataset.full) {
        var hi = new Image();
        hi.onload = function () {
          active.src = active.dataset.full;
          if (active.dataset.srcset) active.srcset = active.dataset.srcset;
        };
        hi.src = active.dataset.full;
      }
      if (counter) counter.textContent = pad(idx + 1) + ' / ' + pad(total);
    }
    function open(startIdx) {
      box.classList.add('is-open');
      box.setAttribute('aria-hidden', 'false');
      document.body.classList.add('kala-lightbox-open');
      setIndex(startIdx || 0);
    }
    function close() {
      box.classList.remove('is-open');
      box.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('kala-lightbox-open');
    }

    // Wire main image to open at the currently-displayed media
    triggers.forEach(function (t) {
      t.addEventListener('click', function (e) {
        // Find the index based on the currently active thumb (if any)
        var startAt = 0;
        var activeThumb = document.querySelector('.kala-product__thumb.is-active');
        if (activeThumb) {
          var all = Array.from(document.querySelectorAll('.kala-product__thumb'));
          startAt = all.indexOf(activeThumb);
          if (startAt < 0) startAt = 0;
        }
        e.preventDefault();
        open(startAt);
      });
    });

    if (prevBtn) prevBtn.addEventListener('click', function () { setIndex(idx - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { setIndex(idx + 1); });
    if (closeBtn) closeBtn.addEventListener('click', close);

    // Click on overlay (not on slide/button) closes
    box.addEventListener('click', function (e) {
      if (e.target === box || e.target === stage) close();
    });

    // Keyboard nav
    document.addEventListener('keydown', function (e) {
      if (!box.classList.contains('is-open')) return;
      if (e.key === 'Escape')      close();
      else if (e.key === 'ArrowLeft')  setIndex(idx - 1);
      else if (e.key === 'ArrowRight') setIndex(idx + 1);
    });

    // Swipe on touch
    var startX = 0, dx = 0;
    stage.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
    stage.addEventListener('touchmove',  function (e) { dx = e.touches[0].clientX - startX; }, { passive: true });
    stage.addEventListener('touchend',   function () {
      if (Math.abs(dx) > 60) setIndex(dx < 0 ? idx + 1 : idx - 1);
      dx = 0;
    });
  }

  /* ──────────────────────────────────────────────
     BOOT
     ────────────────────────────────────────────── */
  /* --------------------------------------------------
    14. SCROLL PROGRESS - always-on signature
    CDN-free + reduce-motion-safe (a progress indicator, not vestibular
    motion). Guarantees a visible premium cue for every visitor.
     -------------------------------------------------- */
  function initScrollProgress() {
    if (document.querySelector('.kala-scroll-progress')) return;
    var bar = document.createElement('div');
    bar.className = 'kala-scroll-progress';
    bar.setAttribute('aria-hidden', 'true');
    var fill = document.createElement('span');
    fill.className = 'kala-scroll-progress__fill';
    bar.appendChild(fill);
    document.body.appendChild(bar);

    var ticking = false;
    function update() {
      var doc = document.documentElement;
      var max = (doc.scrollHeight - doc.clientHeight) || 1;
      var sy = window.scrollY || window.pageYOffset || doc.scrollTop || 0;
      var p = Math.min(1, Math.max(0, sy / max));
      fill.style.transform = 'scaleX(' + p.toFixed(4) + ')';
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* --------------------------------------------------
    15. BOOT SIGNATURE - makes the cinema layer observable
    Prints one console line + tags <html data-kala-cinema="on"> so the
    state can be confirmed in 1 glance (DevTools) without guesswork.
     -------------------------------------------------- */
  function bootSignature() {
    var state = {
      reduceMotion: reduceMotion,
      fineHover: fineHover,
      lenis: !!window.Lenis,
      gsap: !!window.gsap,
      scrollTrigger: !!window.ScrollTrigger,
      three: !!window.THREE
    };
    try {
      document.documentElement.setAttribute('data-kala-cinema', 'on');
      document.documentElement.setAttribute('data-kala-motion', reduceMotion ? 'reduced' : 'full');
      window.__kalaCinema = state;
      console.info(
        '%c T&S %c cinema layer active',
        'background:#060606;color:#F2D88B;padding:2px 7px;border-radius:2px;font-weight:700;letter-spacing:.08em;',
        'color:#C9A14A;font-weight:600;',
        state
      );
      if (reduceMotion) {
        console.info('%c[T&S] Reduce Motion is ON - cursor / parallax / WebGL are intentionally disabled. Turn it off (System Settings > Accessibility > Display > Reduce Motion) to see the full experience.', 'color:#9a7b2e;');
      }
    } catch (e) {}
  }

  /* ──────────────────────────
     16. 3D TILT - perspective tilt on [data-tilt]
     ────────────────────────── */
  function initTilt() {
    if (reduceMotion || !fineHover) return;
    document.querySelectorAll('[data-tilt]').forEach(function (el) {
      if (el.dataset.tiltReady) return;
      el.dataset.tiltReady = '1';
      var max = parseFloat(el.dataset.tilt) || 8;
      var persp = parseFloat(el.dataset.tiltPersp) || 900;
      var scale = parseFloat(el.dataset.tiltScale) || 1.02;
      var rx = 0, ry = 0, trx = 0, tryY = 0, raf = null, rect = null, hovering = false;
      var glare = null;
      if (el.hasAttribute('data-tilt-glare')) {
        glare = document.createElement('span');
        glare.className = 'kala-tilt-glare';
        glare.setAttribute('aria-hidden', 'true');
        el.appendChild(glare);
      }
      function tick() {
        trx += (rx - trx) * 0.12;
        tryY += (ry - tryY) * 0.12;
        var settled = Math.abs(rx - trx) < 0.02 && Math.abs(ry - tryY) < 0.02;
        if (!hovering && settled) {
          el.style.transform = '';
          el.classList.remove('is-tilting');
          raf = null;
          return;
        }
        var s = hovering ? scale : 1;
        el.style.transform = 'perspective(' + persp + 'px) rotateX(' + trx.toFixed(2) + 'deg) rotateY(' + tryY.toFixed(2) + 'deg) scale(' + s + ')';
        raf = requestAnimationFrame(tick);
      }
      el.addEventListener('mouseenter', function () { hovering = true; rect = el.getBoundingClientRect(); el.classList.add('is-tilting'); });
      el.addEventListener('mousemove', function (e) {
        if (!rect) rect = el.getBoundingClientRect();
        var px = (e.clientX - rect.left) / rect.width;
        var py = (e.clientY - rect.top) / rect.height;
        ry = (px - 0.5) * 2 * max;
        rx = -(py - 0.5) * 2 * max;
        if (glare) { glare.style.setProperty('--gx', (px * 100).toFixed(1) + '%'); glare.style.setProperty('--gy', (py * 100).toFixed(1) + '%'); glare.style.opacity = '1'; }
        if (!raf) raf = requestAnimationFrame(tick);
      });
      el.addEventListener('mouseleave', function () { hovering = false; rx = 0; ry = 0; rect = null; if (glare) glare.style.opacity = '0'; if (!raf) raf = requestAnimationFrame(tick); });
    });
  }

  /* ──────────────────────────
     17. SCROLL-SCRUBBED 3D DEPTH - [data-scrub]
     ────────────────────────── */
  function initScrub() {
    if (reduceMotion) return;
    waitFor(function () { return window.gsap && window.ScrollTrigger; }, 6000, function (ok) {
      if (!ok) return;
      var gsap = window.gsap;
      gsap.registerPlugin(window.ScrollTrigger);
      document.querySelectorAll('[data-scrub]').forEach(function (el) {
        if (el.dataset.scrubReady) return;
        el.dataset.scrubReady = '1';
        el.style.transitionProperty = 'opacity';
        var ry = parseFloat(el.getAttribute('data-scrub-rotatey')) || 0;
        var rxv = parseFloat(el.getAttribute('data-scrub-rotatex')) || 0;
        var fromScale = parseFloat(el.getAttribute('data-scrub-scale')) || 1;
        var fromY = parseFloat(el.getAttribute('data-scrub-y')) || 0;
        gsap.fromTo(el,
          { rotateY: ry, rotateX: rxv, scale: fromScale, y: fromY, transformPerspective: 1200, transformOrigin: '50% 50%' },
          { rotateY: 0, rotateX: 0, scale: 1, y: 0, ease: 'none',
            scrollTrigger: { trigger: el, start: 'top 88%', end: 'top 38%', scrub: 0.8 } }
        );
      });
    });
  }

  ready(function () {
    bootSignature();
    initScrollProgress();
    initLineReveal();
    initImagePeek();
    initStitchDrawer();
    initCursor();
    initMagnetic();
    initCardFlip();
    initWebGLHero();
    initLightbox();
    applyVTNames();
    initLenis();
    initGSAP();
    initScrub();
    initTilt();
    initViewTransitions();
  });
})();
